import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CoherenceDataGrid, CoherenceDataGridColumn } from "@coherence-design-system/controls";

import { IMenuItemDb } from "@dining/api-client";

// ⚠️ REQUIRED by CoherenceDataGrid (Fluent v8)
import { CheckboxVisibility, Selection, SelectionMode } from "@fluentui/react";

// ✅ Fluent UI v9
import { Text, makeStyles } from "@fluentui/react-components";

import { setSelectedItem } from "../../reducers/diningReducer";
import { currencySelector, selectedItemSelector } from "../../selectors/diningSelector";

import styles from "../../styles/content.module.css";
import { EditDeleteColumn } from "./EditDeleteColumn";

const useStyles = makeStyles({
  nameCell: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  description: {
    fontSize: "12px",
    opacity: 0.7,
  },
});

export const ItemList = (props: { itemBank: IMenuItemDb[] }) => {
  const dispatch = useDispatch();
  const currentCurrency = useSelector(currencySelector);
  const selectedItem = useSelector(selectedItemSelector);
  const classes = useStyles();

  interface MenuItemDbWithKey extends IMenuItemDb {
    key: string;
  }

  const bankWithKeys: MenuItemDbWithKey[] = React.useMemo(
    () => props.itemBank.map((item) => ({ ...item, key: item.id })),
    [props.itemBank],
  );

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const [selection] = React.useState(
    () =>
      new Selection<MenuItemDbWithKey>({
        items: [],
        onSelectionChanged: () => {
          if (selection.getSelectedCount() === 1) {
            dispatchRef.current(setSelectedItem(selection.getSelection()[0]));
          } else {
            dispatchRef.current(setSelectedItem(undefined));
          }
        },
      }),
  );

  useEffect(() => {
    selection.setItems(bankWithKeys, false);
  }, [bankWithKeys, selection]);

  useEffect(() => {
    if (!selectedItem) {
      selection.setAllSelected(false);
    }
  }, [selectedItem, selection]);

  const columns: CoherenceDataGridColumn<IMenuItemDb>[] = [
    {
      key: "name",
      name: "Item Name",
      type: "string",
      fieldName: "name",
      minColumnWidth: 100,
      isResizable: true,
      isRowHeader: true,
      getValue: (item) => item.name,
      onRender: (item: IMenuItemDb) => (
        <div className={classes.nameCell}>
          <Text weight="semibold">{item.name}</Text>
          <Text className={classes.description}>{item.description}</Text>
        </div>
      ),
    },
    {
      key: "category",
      name: "Category",
      fieldName: "category",
      type: "string",
      minColumnWidth: 100,
      isResizable: true,
    },
    {
      key: "price",
      name: "Price",
      fieldName: "amount",
      type: "currency",
      minColumnWidth: 90,
      isResizable: true,
      onRender: (item: IMenuItemDb) => (
        <Text>
          {currentCurrency.currency}
          {item.amount}
        </Text>
      ),
    },
    {
      key: "editDelete",
      name: "Options",
      type: "custom",
      isIconOnly: true,
      minColumnWidth: 100,
      isResizable: true,
      onRender: (item: IMenuItemDb) => <EditDeleteColumn selectedGridDataGUID={item.id} />,
    },
  ];

  return (
    <div className={styles.itemGridSpacing}>
      <CoherenceDataGrid
        listConfig={columns}
        data={props.itemBank}
        isSortable
        isScrollable
        selection={selection}
        selectionMode={SelectionMode.single}
        checkButtonAriaLabel="select row"
        selectionPreservedOnEmptyClick={false}
        checkboxVisibility={CheckboxVisibility.hidden}
        useFastIcons={false}
      />
    </div>
  );
};
